import React, { useState } from "react";
import {
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { toast } from "react-toastify";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";

// Estilos customizados
const useStyles = makeStyles((theme) => ({
  codeBox: {
    backgroundColor: "#424242",
    padding: theme.spacing(2),
    borderRadius: "8px",
    marginTop: theme.spacing(2),
    fontSize: "1rem",
    lineHeight: "1.5",
    maxHeight: "400px",
    overflowY: "auto",
    position: "relative",
  },
  selectBox: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  copyIcon: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    color: "#ffffff",
    padding: theme.spacing(0.5),
    borderRadius: "4px",
    zIndex: 10,
    transition: "background-color 0.3s",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
    },
  },
}));

// Exemplos de código com URL dinâmica
const examples = {
  javascript: `const sendMessage = async () => {
  const token = "seu_token_aqui";
  const url = "${process.env.REACT_APP_BACKEND_URL}"; // Obtida do .env
  const data = {
    number: "558599999999",
    body: "Olá, tudo bem?",
    userId: "1",
    queueId: "1",
    sendSignature: true,
    closeTicket: false,
  };
  try {
    const response = await axios.post(url, data, {
      headers: {
        Authorization: \`Bearer \${token}\`,
        "Content-Type": "application/json",
      },
    });
    console.log("Resposta:", response.data);
  } catch (error) {
    console.error("Erro ao enviar mensagem:", error);
  }
};`,

  php: `<?php
$token = "seu_token_aqui";
$url = "${process.env.REACT_APP_BACKEND_URL}"; // Obtida do .env
$data = [
  'number' => '558599999999',
  'body' => 'Olá, tudo bem?',
  'userId' => '1',
  'queueId' => '1',
  'sendSignature' => true,
  'closeTicket' => false,
];

$options = [
  'http' => [
    'header'  => "Authorization: Bearer " . $token . "\\r\\n" .
                 "Content-type: application/json\\r\\n",
    'method'  => 'POST',
    'content' => json_encode($data),
  ],
];

$context  = stream_context_create($options);
$result = file_get_contents($url, false, $context);
if ($result === FALSE) {
  // Handle error
}

echo $result;
?>`,

  python: `import requests

token = "seu_token_aqui"
url = "${process.env.REACT_APP_BACKEND_URL}"  # Obtida do .env

data = {
    "number": "558599999999",
    "body": "Olá, tudo bem?",
    "userId": "1",
    "queueId": "1",
    "sendSignature": True,
    "closeTicket": False,
}

headers = {
    "Authorization": f"Bearer {token}",
    "Content-Type": "application/json",
}

response = requests.post(url, json=data, headers=headers)
print(response.json())`,

  java: `import java.net.HttpURLConnection;
import java.net.URL;
import java.io.OutputStream;

public class SendMessage {
    public static void main(String[] args) throws Exception {
        String token = "seu_token_aqui";
        String url = "${process.env.REACT_APP_BACKEND_URL}"; // Obtida do .env

        String jsonInputString = "{" +
                "\\"number\\": \\"558599999999\\"," +
                "\\"body\\": \\"Olá, tudo bem?\\"," +
                "\\"userId\\": \\"1\\"," +
                "\\"queueId\\": \\"1\\"," +
                "\\"sendSignature\\": true," +
                "\\"closeTicket\\": false" +
                "}";

        URL obj = new URL(url);
        HttpURLConnection con = (HttpURLConnection) obj.openConnection();
        con.setRequestMethod("POST");
        con.setRequestProperty("Authorization", "Bearer " + token);
        con.setRequestProperty("Content-Type", "application/json");
        con.setDoOutput(true);

        try (OutputStream os = con.getOutputStream()) {
            byte[] input = jsonInputString.getBytes("utf-8");
            os.write(input, 0, input.length);
        }

        int responseCode = con.getResponseCode();
        System.out.println("POST Response Code :: " + responseCode);
    }
};`,

  csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Threading.Tasks;

public class SendMessage {
    private static readonly HttpClient client = new HttpClient();
    public static async Task Main(string[] args) {
        var token = "seu_token_aqui";
        var url = "${process.env.REACT_APP_BACKEND_URL}"; // Obtida do .env

        var json = "{" +
                   "\\"number\\": \\"558599999999\\"," +
                   "\\"body\\": \\"Olá, tudo bem?\\"," +
                   "\\"userId\\": \\"1\\"," +
                   "\\"queueId\\": \\"1\\"," +
                   "\\"sendSignature\\": true," +
                   "\\"closeTicket\\": false" +
                   "}";

        var content = new StringContent(json, Encoding.UTF8, "application/json");
        client.DefaultRequestHeaders.Add("Authorization", "Bearer " + token);
        var response = await client.PostAsync(url, content);

        Console.WriteLine("Response: " + await response.Content.ReadAsStringAsync());
    }
};`,

  cpp: `#include <iostream>
#include <string>
#include <curl/curl.h>

int main() {
    std::string token = "seu_token_aqui";
    std::string url = "${process.env.REACT_APP_BACKEND_URL}"; // Obtida do .env
    std::string data = "{\\"number\\":\\"558599999999\\",\\"body\\":\\"Olá, tudo bem?\\",\\"userId\\":\\"1\\",\\"queueId\\":\\"1\\",\\"sendSignature\\":true,\\"closeTicket\\":false}";

    CURL* curl = curl_easy_init();
    if (curl) {
        struct curl_slist* headers = NULL;
        headers = curl_slist_append(headers, ("Authorization: Bearer " + token).c_str());
        headers = curl_slist_append(headers, "Content-Type: application/json");

        curl_easy_setopt(curl, CURLOPT_URL, url.c_str());
        curl_easy_setopt(curl, CURLOPT_POSTFIELDS, data.c_str());
        curl_easy_setopt(curl, CURLOPT_HTTPHEADER, headers);

        CURLcode res = curl_easy_perform(curl);
        if (res != CURLE_OK) {
            std::cerr << "Request failed: " << curl_easy_strerror(res) << std::endl;
        }
        curl_easy_cleanup(curl);
    }
}`,

  go: `package main

import (
  "bytes"
  "encoding/json"
  "fmt"
  "net/http"
)

func main() {
  token := "seu_token_aqui"
  url := "${process.env.REACT_APP_BACKEND_URL}" // Obtida do .env

  data := map[string]interface{}{
    "number":        "558599999999",
    "body":          "Olá, tudo bem?",
    "userId":        "1",
    "queueId":       "1",
    "sendSignature": true,
    "closeTicket":   false,
  }
  jsonData, _ := json.Marshal(data)

  req, _ := http.NewRequest("POST", url, bytes.NewBuffer(jsonData))
  req.Header.Set("Authorization", "Bearer "+token)
  req.Header.Set("Content-Type", "application/json")

  client := &http.Client{}
  resp, err := client.Do(req)
  if err != nil {
    fmt.Println("Erro:", err)
    return
  }
  defer resp.Body.Close()
  fmt.Println("Response Status:", resp.Status)
}`,
};

const IntegrationExamples = () => {
  const classes = useStyles();
  const [selectedLanguage, setSelectedLanguage] = useState("javascript");

  const handleChange = (event) => {
    setSelectedLanguage(event.target.value);
  };

  const copyToClipboard = () => {
    const codeToCopy = examples[selectedLanguage];
    navigator.clipboard.writeText(codeToCopy);
    toast.success("Exemplo copiado para a área de transferência!");
  };

  return (
    <Box>
      <FormControl className={classes.selectBox}>
        <InputLabel>Linguagem de Programação</InputLabel>
        <Select value={selectedLanguage} onChange={handleChange}>
          {Object.keys(examples).map((lang) => (
            <MenuItem key={lang} value={lang}>
              {lang.toUpperCase()}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box className={classes.codeBox}>
        <Tooltip title="Copiar código">
          <IconButton className={classes.copyIcon} onClick={copyToClipboard}>
            <FileCopyIcon />
          </IconButton>
        </Tooltip>
        <SyntaxHighlighter
          language={selectedLanguage}
          style={materialDark}
        >
          {examples[selectedLanguage]}
        </SyntaxHighlighter>
      </Box>
    </Box>
  );
};

export default IntegrationExamples;
